import { type FC, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ComplianceRecordKindType } from "@/types/graphql/partner.generated";
import type { ComplianceRecord } from "./ComplianceRecordsList";

export interface ComplianceRecordFormValues {
  kind: ComplianceRecordKindType;
  file: File | null;
  url: string;
  performerName: string;
  note: string;
}

interface ComplianceRecordFormDialogProps {
  open: boolean;
  record: ComplianceRecord | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: ComplianceRecordFormValues) => void;
}

const EMPTY: ComplianceRecordFormValues = {
  kind: ComplianceRecordKindType.FileType,
  file: null,
  url: "",
  performerName: "",
  note: "",
};

export const ComplianceRecordFormDialog: FC<
  ComplianceRecordFormDialogProps
> = ({ open, record, saving, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<ComplianceRecordFormValues>(EMPTY);

  useEffect(() => {
    if (!open) return;
    setValues(
      record
        ? {
            kind: record.kind,
            file: null,
            url: record.url ?? "",
            performerName: record.performerName ?? "",
            note: record.note ?? "",
          }
        : EMPTY
    );
  }, [open, record]);

  const isEdit = Boolean(record);
  // Вид записи после создания не меняется: файл нельзя превратить в
  // ссылку, не потеряв сам файл. Поэтому при редактировании проверяем
  // только ссылку: у LINK-записи её нельзя стереть в пустоту, а файл
  // остаётся тем же, что и был загружен при создании.
  const canSubmit =
    values.kind === ComplianceRecordKindType.LinkType
      ? Boolean(values.url.trim())
      : isEdit || Boolean(values.file);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("compliance.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label={t("compliance.kind.label")}
            value={values.kind}
            disabled={isEdit}
            onChange={(event) =>
              setValues((prev) => ({
                ...prev,
                kind: event.target.value as ComplianceRecordKindType,
              }))
            }
          >
            <MenuItem value={ComplianceRecordKindType.FileType}>
              {t("compliance.kind.FILE")}
            </MenuItem>
            <MenuItem value={ComplianceRecordKindType.LinkType}>
              {t("compliance.kind.LINK")}
            </MenuItem>
          </TextField>

          {values.kind === ComplianceRecordKindType.FileType && !isEdit && (
            <Button component="label" variant="outlined">
              {values.file?.name || t("compliance.chooseFile")}
              <input
                type="file"
                hidden
                onChange={(event) =>
                  setValues((prev) => ({
                    ...prev,
                    file: event.target.files?.[0] ?? null,
                  }))
                }
              />
            </Button>
          )}

          {values.kind === ComplianceRecordKindType.LinkType && (
            <TextField
              label={t("compliance.url")}
              value={values.url}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, url: event.target.value }))
              }
            />
          )}

          <TextField
            label={t("compliance.performerName")}
            value={values.performerName}
            onChange={(event) =>
              setValues((prev) => ({
                ...prev,
                performerName: event.target.value,
              }))
            }
          />

          <TextField
            label={t("compliance.note")}
            multiline
            minRows={2}
            value={values.note}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, note: event.target.value }))
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("compliance.cancel")}</Button>
        <Button
          variant="contained"
          disabled={saving || !canSubmit}
          onClick={() => onSubmit(values)}
        >
          {t("compliance.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
